<template>
    <Transition>
        <Teleport to="#modals">
            <Modal
                v-show="active"
                :active="active"
                title="Leave a review"
                @close="close"
            >
                <div class="space-y-4 sm:space-y-7 pt-4 sm:pt-7 border-t border-brand-2-950/10">
                    <FormSuccess :message="successMessage" />
                    <FormError :message="errorMessage" />

                    <form
                        ref="formElement"
                        @submit.prevent="submitForm"
                    >
                        <div class="form-row">
                            <div class="form-col form-col-2">
                                <label
                                    for="review-name"
                                    class="label"
                                >
                                    Full name
                                </label>
                                <input
                                    id="review-name"
                                    class="input"
                                    type="text"
                                    required
                                    v-model="form.name"
                                >
                            </div>

                            <div class="form-col form-col-2">
                                <label
                                    for="review-email"
                                    class="label"
                                >
                                    Email address
                                </label>
                                <input
                                    id="review-email"
                                    class="input"
                                    type="email"
                                    required
                                    v-model="form.email"
                                >
                            </div>

                            <div class="form-col">
                                <label
                                    for="review-date"
                                    class="label"
                                >
                                    Date of stay
                                </label>

                                <Datepicker
                                    v-if="everOpened"
                                    id="review-date"
                                    mode="single"
                                    :showMonths="1"
                                    :inline="false"
                                    v-model:startDate="form.date"
                                />
                            </div>

                            <div class="form-col">
                                <StarRating v-model:rating="form.rating" />
                            </div>

                            <div class="form-col">
                                <label
                                    for="review"
                                    class="label"
                                >
                                    Share details of your stay
                                </label>
                                <textarea
                                    id="review"
                                    class="textarea"
                                    required
                                    v-model="form.review"
                                ></textarea>
                            </div>

                            <div class="form-col">
                                <div>
                                    <button
                                        class="button button-2"
                                        :disabled="submittingForm"
                                    >
                                        Submit your review

                                        <span
                                            v-if="submittingForm"
                                            class="size-4 text-white"
                                            v-html="SpinnerIcon"
                                        ></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </Modal>
        </Teleport>
    </Transition>
</template>

<script setup>
    import { ref, useTemplateRef, watch, defineAsyncComponent } from 'vue';
    import axios from 'axios';

    import Modal from '@js/src/components/Modal.vue';
    import FormSuccess from '@js/src/components/FormSuccess.vue';
    import FormError from '@js/src/components/FormError.vue';
    import StarRating from '@js/src/components/StarRating.vue';

    import SpinnerIcon from '@img/icons/spinner.svg?raw';

    // Async: the modal body is v-show, so a static import initialised flatpickr
    // at page load for a form nobody had opened. Loads on first open instead.
    const Datepicker = defineAsyncComponent(() => import('@js/src/components/Datepicker.vue'));

    const props = defineProps([
        'active',
        'propertyId',
    ]);

    // Sticky, so the field (and the visitor's part-filled form) survives
    // close/reopen exactly as it did when the modal was only ever v-show hidden.
    const everOpened = ref(false);

    watch(() => props.active, (active) => {
        if (active) {
            everOpened.value = true;
        }
    });

    const emits = defineEmits([
        'close',
    ]);

    const formRef = useTemplateRef('formElement');

    const submittingForm = ref(false);

    const successMessage = ref('');
    const errorMessage = ref('');

    const form = ref({
        name: '',
        email: '',
        date: '',
        rating: 0,
        review: '',
    });

    function submitForm() {
        successMessage.value = '';
        errorMessage.value = '';
        submittingForm.value = true;

        axios.post(`/wp-json/lh/v1/properties/${props.propertyId}/reviews`, form.value, {
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(response => {
                successMessage.value = response.data.message;
                errorMessage.value = '';

                form.value = {
                    name: '',
                    email: '',
                    date: '',
                    rating: 1,
                    review: '',
                };

                formRef.value.classList.add('hidden');
            })
            .catch(error => {
                const responseErrors = error?.response?.data?.data?.errors || {};
                const firstError = Object.values(responseErrors)[0] || '';

                successMessage.value = '';
                errorMessage.value = Object.values(firstError)[0] ?
                    Object.values(firstError)[0] :
                    (error?.response?.data?.message || 'Sorry, an error occurred.');
            })
            .finally(() => {
                submittingForm.value = false;
            });
    }

    function close() {
        emits('close');

        setTimeout(() => {
            successMessage.value = '';
            errorMessage.value = '';
            formRef.value.classList.remove('hidden');
        }, 500);
    }
</script>
